/**
 * A aba Preventivas: as rotinas da casa, com a próxima visita vencida marcada.
 *
 * Papel: reproduz o painel `#panel-preventivas` do SM (l. 614–635) e
 * `renderPreventivas` (l. 1010–1055): busca em elemento, atividade e empresa —
 * não em responsável nem em observações —, status de escolha única, período
 * sobre a próxima visita, doze colunas, onze ordenáveis.
 *
 * Como usar: a página passa as preventivas da casa, sem filtro, a casa para o
 * formulário gravar, e `podeEditar`, que esconde "+ Novo registro" e "Editar".
 *
 * **Vencida** é a próxima visita estritamente anterior a `hojeEmIso()`, em hora
 * local — a de hoje não é —, qualquer que seja o status. Ela aparece como a
 * origem a desenha de fato: na cor e no peso das outras células da coluna, só
 * com " ⚠" depois da data. A `.overdue-text` vermelha da origem perde na cascata
 * para `table.data td.cell-mu` e nunca aparece; pintar a vencida aqui mostraria
 * ao escritório o que ele nunca viu. Não há faixa de proximidade: só vencida ou
 * não. O "#" ordena como número, com o texto que não é número junto do vazio, e
 * a periodicidade em ordem alfabética do texto.
 */
import { type Preventiva, type StatusPreventiva } from '@concierge/shared';
import { useState } from 'react';

import {
  CabecalhoOrdenavel,
  type ColunaOrdenavel,
  FiltroDePeriodo,
  dentroDoPeriodo,
  useOrdenacao,
} from '@/shared/embutido';
import { cn } from '@/shared/lib/utils';
import { hojeEmIso } from '@/shared/lib/vencimento';
import {
  estaVencida,
  formatarCustoDaTabela,
  formatarDataDaTabela,
  passaNaBusca,
  situacaoDaLinhaVazia,
  valorDoNumero,
} from '../regras';
import {
  ORDEM_DOS_STATUS_PREVENTIVA,
  ROTULOS_STATUS_PREVENTIVA,
  TOM_DO_SELO_PREVENTIVA,
} from '../rotulos';
import { type SituacaoDaLista } from '../types';
import { FormularioPreventiva } from './formulario-preventiva';
import { BarraDeFiltros, Espacador } from './kit/barra-de-filtros';
import { Botao } from './kit/botao';
import { BotaoDaBarra } from './kit/botao-da-barra';
import {
  BuscaDaBarra,
  CLASSE_DA_DATA_DA_BARRA,
  CLASSE_DO_ROTULO_DA_BARRA,
  SelectDaBarra,
} from './kit/campos-da-barra';
import { FaixaDoCartao } from './kit/faixa-do-cartao';
import { Selo } from './kit/selo';
import {
  CLASSE_DO_CABECALHO_ORDENAVEL,
  Celula,
  CelulaDeCabecalho,
  LinhaDaTabela,
  LinhaVazia,
  Tabela,
} from './kit/tabela';

/** Como cada coluna ordena; o status pelo rótulo, as visitas pela data. */
const COLUNAS: Readonly<Record<string, ColunaOrdenavel<Preventiva>>> = {
  numero: { tipo: 'numero', valor: (preventiva) => valorDoNumero(preventiva.numero) },
  elemento: { tipo: 'texto', valor: (preventiva) => preventiva.elemento },
  atividade: { tipo: 'texto', valor: (preventiva) => preventiva.atividade },
  status: {
    tipo: 'texto',
    valor: (preventiva) => ROTULOS_STATUS_PREVENTIVA[preventiva.status],
  },
  periodicidade: { tipo: 'texto', valor: (preventiva) => preventiva.periodicidade },
  empresa: { tipo: 'texto', valor: (preventiva) => preventiva.empresa },
  responsavel: { tipo: 'texto', valor: (preventiva) => preventiva.responsavel },
  ultimaVisita: { tipo: 'data', valor: (preventiva) => preventiva.ultimaVisita },
  proximaVisita: { tipo: 'data', valor: (preventiva) => preventiva.proximaVisita },
  custo: { tipo: 'numero', valor: (preventiva) => preventiva.custo },
  observacao: { tipo: 'texto', valor: (preventiva) => preventiva.observacao },
};

/** Os cabeçalhos ordenáveis, na ordem e com a largura da origem (l. 631–632). */
const CABECALHOS = [
  { chave: 'numero', rotulo: '#', largura: 'w-[3%]' },
  { chave: 'elemento', rotulo: 'ELEMENTO / COMPONENTE', largura: 'w-[11%]' },
  { chave: 'atividade', rotulo: 'ATIVIDADE', largura: 'w-[11%]' },
  { chave: 'status', rotulo: 'STATUS', largura: 'w-[12%]' },
  { chave: 'periodicidade', rotulo: 'PERIODICIDADE', largura: 'w-[6%]' },
  { chave: 'empresa', rotulo: 'EMPRESA', largura: 'w-[7%]' },
  { chave: 'responsavel', rotulo: 'RESPONSÁVEL', largura: 'w-[8%]' },
  { chave: 'ultimaVisita', rotulo: 'ÚLT. VISITA', largura: 'w-[6%]' },
  { chave: 'proximaVisita', rotulo: 'PRÓX. VISITA', largura: 'w-[6%]' },
  { chave: 'custo', rotulo: 'CUSTO (R$)', largura: 'w-[6%]' },
  { chave: 'observacao', rotulo: 'OBSERVAÇÕES', largura: 'w-[11%]' },
] as const;

/** A caixa do formulário: fechada, ou aberta criando ou editando. */
type CaixaAberta = { aberta: false } | { aberta: true; preventiva: Preventiva | null };

interface AbaPreventivasProps {
  /** As preventivas da casa, sem filtro. */
  preventivas: readonly Preventiva[];
  situacao: SituacaoDaLista;
  /** Relê a lista depois de uma falha. */
  onTentarDeNovo: () => void;
  /** A casa em que o formulário grava. */
  residenciaId: string;
  /** `false` para quem só tem `ver`. */
  podeEditar: boolean;
}

/**
 * A aba Preventivas.
 *
 * @param preventivas - As preventivas da casa.
 * @param situacao - A situação da consulta.
 * @param onTentarDeNovo - Relê a lista.
 * @param residenciaId - A casa do formulário.
 * @param podeEditar - Se mostra "+ Novo registro" e "Editar".
 * @returns A faixa, a barra de filtros, a tabela e, aberta, a caixa.
 */
export function AbaPreventivas({
  preventivas,
  situacao,
  onTentarDeNovo,
  residenciaId,
  podeEditar,
}: AbaPreventivasProps) {
  const [busca, setBusca] = useState('');
  const [status, setStatus] = useState<StatusPreventiva | ''>('');
  const [de, setDe] = useState('');
  const [ate, setAte] = useState('');
  const [caixa, setCaixa] = useState<CaixaAberta>({ aberta: false });
  const hoje = hojeEmIso();

  const filtradas = preventivas.filter(
    (preventiva) =>
      (status === '' || preventiva.status === status) &&
      dentroDoPeriodo(preventiva.proximaVisita, de, ate) &&
      passaNaBusca(busca, [preventiva.elemento, preventiva.atividade, preventiva.empresa]),
  );
  const ordenacao = useOrdenacao(filtradas, COLUNAS);
  const vazia = situacaoDaLinhaVazia(situacao, filtradas.length);

  /**
   * Troca o status escolhido.
   *
   * @param valor - O `value` da opção: vazio ou um status.
   */
  function handleStatus(valor: string) {
    setStatus(ORDEM_DOS_STATUS_PREVENTIVA.find((opcao) => opcao === valor) ?? '');
  }

  return (
    <div>
      <FaixaDoCartao>
        {'VILLA PRADO\u00A0\u00A0·\u00A0\u00A0MANUTENÇÕES PREVENTIVAS'}
      </FaixaDoCartao>
      <BarraDeFiltros>
        <BuscaDaBarra
          placeholder="Buscar elemento, atividade, empresa..."
          value={busca}
          onChange={(evento) => setBusca(evento.target.value)}
        />
        <SelectDaBarra
          aria-label="Status"
          value={status}
          onChange={(evento) => handleStatus(evento.target.value)}
        >
          <option value="">Todos os status</option>
          {ORDEM_DOS_STATUS_PREVENTIVA.map((opcao) => (
            <option key={opcao} value={opcao}>
              {ROTULOS_STATUS_PREVENTIVA[opcao]}
            </option>
          ))}
        </SelectDaBarra>
        <FiltroDePeriodo
          rotulo="Próx. visita"
          de={de}
          ate={ate}
          onMudarDe={setDe}
          onMudarAte={setAte}
          classeDoRotulo={CLASSE_DO_ROTULO_DA_BARRA}
          classeDoCampo={CLASSE_DA_DATA_DA_BARRA}
        />
        <Espacador />
        {podeEditar && (
          <BotaoDaBarra onClick={() => setCaixa({ aberta: true, preventiva: null })}>
            + Novo registro
          </BotaoDaBarra>
        )}
      </BarraDeFiltros>

      <Tabela>
        <thead>
          <tr>
            {CABECALHOS.map(({ chave, rotulo, largura }) => (
              <CabecalhoOrdenavel
                key={chave}
                seta={ordenacao.seta(chave)}
                onAlternar={() => ordenacao.alternar(chave)}
                className={cn(CLASSE_DO_CABECALHO_ORDENAVEL, largura)}
              >
                {rotulo}
              </CabecalhoOrdenavel>
            ))}
            <CelulaDeCabecalho>
              <span className="sr-only">Ações</span>
            </CelulaDeCabecalho>
          </tr>
        </thead>
        <tbody>
          {vazia !== null ? (
            <LinhaVazia colunas={12} situacao={vazia} onTentarDeNovo={onTentarDeNovo} />
          ) : (
            ordenacao.linhas.map((preventiva) => (
              <LinhaDaTabela key={preventiva.id}>
                <Celula tipo="fraco">{preventiva.numero}</Celula>
                <Celula tipo="titulo">{preventiva.elemento}</Celula>
                <Celula tipo="quebra">{preventiva.atividade}</Celula>
                <Celula>
                  <Selo tom={TOM_DO_SELO_PREVENTIVA[preventiva.status]}>
                    {ROTULOS_STATUS_PREVENTIVA[preventiva.status]}
                  </Selo>
                </Celula>
                <Celula tipo="fraco">{preventiva.periodicidade}</Celula>
                <Celula tipo="fraco">{preventiva.empresa}</Celula>
                <Celula tipo="fraco">{preventiva.responsavel}</Celula>
                <Celula tipo="fraco">{formatarDataDaTabela(preventiva.ultimaVisita)}</Celula>
                <Celula tipo="fraco">
                  {formatarDataDaTabela(preventiva.proximaVisita)}
                  {estaVencida(preventiva.proximaVisita, hoje) ? ' ⚠' : ''}
                </Celula>
                <Celula tipo="numero">{formatarCustoDaTabela(preventiva.custo)}</Celula>
                <Celula tipo="quebra">{preventiva.observacao}</Celula>
                <Celula tipo="acoes">
                  {podeEditar && (
                    <Botao
                      variante="fantasma"
                      pequeno
                      onClick={() => setCaixa({ aberta: true, preventiva })}
                    >
                      Editar
                    </Botao>
                  )}
                </Celula>
              </LinhaDaTabela>
            ))
          )}
        </tbody>
      </Tabela>

      {caixa.aberta && (
        <FormularioPreventiva
          key={caixa.preventiva?.id ?? 'nova'}
          preventiva={caixa.preventiva}
          residenciaId={residenciaId}
          onFechar={() => setCaixa({ aberta: false })}
        />
      )}
    </div>
  );
}
